import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { WhiteLabelService } from '@flexyoo/common';
import { EmployeeSelectedBenefit } from '@flexyoo/employee/models';
import { getBenefitDisplayAmount } from '@flexyoo/employee/util';
import { FlexyooNumberPipe } from '@flexyoo/shared/pipes';
import { TranslateModule } from '@ngx-translate/core';
import { EmployeeFinancingPlanBadgeComponent } from '../employee-financing-plan-badge/employee-financing-plan-badge.component';

@Component({
  selector: 'flexyoo-employee-basket-cart-management-row',
  templateUrl: './employee-basket-cart-management-row.component.html',
  styleUrl: './employee-basket-cart-management-row.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateModule,
    MatIconModule,
    MatTooltip,
    FlexyooNumberPipe,
    EmployeeFinancingPlanBadgeComponent,
  ],
})
export class EmployeeBasketCartManagementRowComponent {
  protected readonly wls = inject(WhiteLabelService);

  readonly benefit = input.required<EmployeeSelectedBenefit>();
  readonly isSelected = input<boolean>(false);
  readonly showPb = input<boolean>(false);
  readonly showSs = input<boolean>(false);
  readonly showRemove = input<boolean>(false);
  readonly showReuploadWarning = input<boolean>(false);

  readonly remove = output<EmployeeSelectedBenefit>();
  readonly reupload = output<EmployeeSelectedBenefit>();
  readonly open = output<EmployeeSelectedBenefit>();

  protected readonly pbGrossAmount = computed(() => this.benefit().pbGrossAmount ?? 0);
  protected readonly ssGrossAmount = computed(() => this.benefit().ssPeriodBudget ?? 0);
  protected readonly displayAmount = computed(() => getBenefitDisplayAmount(this.benefit()));
  protected readonly isRejected = computed(() => this.benefit().status === 'cancelled');
  // A rejected benefit keeps its single struck-through total instead of the P&B / SS split
  protected readonly pbAmount = computed<number | null>(() => {
    if (!this.isRejected()) {
      return this.pbGrossAmount();
    }

    return this.showSs() ? null : this.displayAmount();
  });
  protected readonly ssAmount = computed(() =>
    this.isRejected() ? this.displayAmount() : this.ssGrossAmount()
  );
  protected readonly rejectedOfferReason = computed(() =>
    this.showReuploadWarning() ? (this.benefit().offerDisapprovedReason ?? null) : null
  );
  protected readonly selectedBackgroundColor = computed(() =>
    this.isSelected() ? 'var(--app-primary-25)' : null
  );

  protected onNameClick(event: MouseEvent): void {
    event.stopPropagation();
    this.open.emit(this.benefit());
  }

  protected onRemoveClick(event: MouseEvent): void {
    event.stopPropagation();
    this.remove.emit(this.benefit());
  }

  protected onReuploadClick(event: MouseEvent): void {
    event.stopPropagation();
    this.reupload.emit(this.benefit());
  }
}
